import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  CompetenciesEditCompetencyModalCompetencyFragment,
  CompetenciesUpdateCompetencyDocument,
  CompetenciesUpdateCompetencyMutationVariables,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CompetencyGroupSelector } from 'modules/competencies/components/CompetencyGroupSelector'

interface Props {
  competency: CompetenciesEditCompetencyModalCompetencyFragment
  onClose: () => void
}

export const EditCompetencyModal: React.FC<Props> = ({ competency, onClose }) => {
  const [update] = useMutation(CompetenciesUpdateCompetencyDocument, {
    extractorFn: (data) => data.competenciesMutations.updateCompetenciesCompetency,
  })

  const form = useForm<CompetenciesUpdateCompetencyMutationVariables>({
    defaultValues: {
      id: competency.id,
      name: competency.name,
      description: competency.description ?? '',
      competencyGroupId: competency.competencyGroup?.id,
    },
    validate(values) {
      const errors: FormErrors<CompetenciesUpdateCompetencyMutationVariables> = {}

      if (values.name == '') {
        errors.name = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await update({
        variables: {
          id: competency.id,
          name: values.name,
          description: values.description,
          competencyGroupId: values.competencyGroupId,
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      illustration="add-first"
      title={i18n.t('competencies_v3.competencies_table.actions.edit')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <TextField
          label={i18n.t('competencies_v3.new_competency_modal.name_input')}
          value={form.values.name}
          onChange={(value: string) => form.setFieldValue('name', value)}
          mandatory
          error={form.touched.name ? form.errors.name : undefined}
        />

        <CompetencyGroupSelector
          value={form.values.competencyGroupId ?? undefined}
          defaultCompetencyGroup={competency.competencyGroup}
          onChange={(groupId) => form.setFieldValue('competencyGroupId', groupId)}
          error={form.touched.competencyGroupId ? form.errors.competencyGroupId : undefined}
        />

        <TextArea
          label={i18n.t('competencies_v3.new_competency_modal.description_input')}
          value={form.values.description ?? ''}
          onChange={(value) => form.setFieldValue('description', value)}
          minRows={4}
          maxHeight="s240"
          error={form.touched.description ? form.errors.description : undefined}
        />
      </Box>
    </Modal>
  )
}
